import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { CreditCard, LogOut, Settings } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { FreeTrialProgressBar } from "~/components/ui/free-trial-progress-bar";
import { UserAvatar } from "~/components/ui/user-avatar";

import { useCreateCheckoutSession } from "~/api/billing/billing";
import { GetOrganization200ServiceStatus, OrganizationRole } from "~/api/model";
import { useGetOrganization } from "~/api/organizations/organizations";
import {
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
} from "~/api/users/users";
import { DEFAULT_FREE_TRIAL_LIMIT } from "~/features/billing/constants";
import { UsageLimitModal } from "~/features/billing/UsageLimitModal";
import { SearchWithShortcut } from "~/features/search/components/SearchWithShortcut";
import { removeAllTokens, ROUTES } from "~/helpers";
import { useAuthStore } from "~/stores/authStore";

import { trackEvent } from "~/utils/analytics";
import { AppLogo } from "./AppLogo";

export const AppHeader = () => {
  const { organizationId } = useParams<{
    organizationId: string;
  }>();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUsageLimitModalOpen, setIsUsageLimitModalOpen] = useState(false);
  const [shortcut] = useState(() =>
    /Win/.test(navigator.platform) ? "Ctrl+K" : "⌘K"
  );

  const { data: userInfo } = useGetCurrentUser();
  const { data: organizationInfo } = useGetOrganization(
    organizationId as string
  );

  const isTrial =
    organizationInfo?.serviceStatus ===
      GetOrganization200ServiceStatus.FREE_TRIAL ||
    organizationInfo?.serviceStatus === null;

  const freeTrialLimit =
    Number(organizationInfo?.freeTrialLimit) || DEFAULT_FREE_TRIAL_LIMIT;

  const isLimitReached =
    (organizationInfo?.pagesUsedInThisBillingPeriod ?? 0) >= freeTrialLimit &&
    organizationInfo?.serviceStatus ===
      GetOrganization200ServiceStatus.FREE_TRIAL;

  const isPaymentRequired =
    organizationInfo?.serviceStatus ===
    GetOrganization200ServiceStatus.PAYMENT_REQUIRED;
  const { mutate: createCheckoutSession } = useCreateCheckoutSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const showSearch = location.pathname !== "/billing/success" && organizationId;

  const onLogoutClick = async () => {
    removeAllTokens();
    useAuthStore.getState().setImpersonating(false);
    queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    await queryClient.cancelQueries();
    queryClient.clear();
    navigate("/login");
  };

  const onPaymentClick = async () => {
    if (!organizationInfo) {
      return;
    }

    if (
      organizationInfo.serviceStatus !== GetOrganization200ServiceStatus.ACTIVE
    ) {
      setIsUsageLimitModalOpen(true);
      return;
    }

    createCheckoutSession(
      { organizationId: organizationId as string, data: {} },
      {
        onSuccess: (data) => {
          if (data.checkoutUrl) {
            window.open(data.checkoutUrl, "_blank");
          }
        },
      }
    );
  };

  const openSearch = useCallback(() => {
    trackEvent({ eventName: "app/any_page/search.button/click" });
    setIsSearchOpen(true);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        trackEvent({ eventName: "app/any_page/CMD+K.shortcut" });
        openSearch();
      }
    },
    [openSearch]
  );

  const handleGoToSettings = () => {
    navigate(ROUTES.SETTINGS({ organizationId }));
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="w-full border-b max-w-[1920px] mx-auto">
      <div className="mx-auto p-3 px-4 sm:px-6 flex h-[64px] items-center justify-between">
        {/* Logo on the left */}
        <div className="flex items-center">
          <Link className="flex items-center" to="/">
            <AppLogo />
          </Link>
        </div>

        {/* User menu on the right */}
        {userInfo && (
          <div className="flex items-center space-x-4">
            {/* Free trial progress bar */}
            {isTrial && (
              <div className="flex items-center gap-2">
                <FreeTrialProgressBar />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onPaymentClick}
                  className="text-xs text-brand hover:text-brand/90 hover:bg-brand/10"
                >
                  <FormattedMessage id="upgrade" defaultMessage="Upgrade" />
                </Button>
              </div>
            )}
            {showSearch && (
              <div className="flex items-center gap-1">
                <MagnifyingGlassIcon
                  className="w-5 h-5 cursor-pointer"
                  onClick={openSearch}
                />
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={openSearch}
                >
                  {shortcut}
                </Badge>
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <UserAvatar
                  name={userInfo?.name || ""}
                  size="lg"
                  className="cursor-pointer"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 mr-4">
                {organizationInfo && (
                  <DropdownMenuLabel className="text-sm">
                    {userInfo.name} <br />
                    <span className="text-sm font-normal">
                      {organizationInfo.name}
                    </span>
                  </DropdownMenuLabel>
                )}
                <DropdownMenuGroup>
                  {organizationId && organizationInfo && (
                    <>
                      {organizationInfo.role !== OrganizationRole.GUEST && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleGoToSettings}>
                            <Settings className="w-4 h-4 mr-2" />
                            <span>
                              <FormattedMessage
                                id="settings"
                                defaultMessage="Settings"
                              />
                            </span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      )}
                      {(organizationInfo.role === OrganizationRole.OWNER ||
                        organizationInfo.role ===
                          OrganizationRole.SUPERADMIN) &&
                        !organizationInfo?.customPlan && (
                          <>
                            <DropdownMenuItem onClick={onPaymentClick}>
                              <CreditCard className="w-4 h-4 mr-2" />
                              <FormattedMessage
                                id="payment"
                                defaultMessage="Billing"
                              />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                    </>
                  )}
                  <DropdownMenuItem destructive onClick={onLogoutClick}>
                    <LogOut className="w-4 h-4 mr-2" />
                    <span>
                      <FormattedMessage id="logout" defaultMessage="Log out" />
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
      {isSearchOpen && <SearchWithShortcut setIsOpen={setIsSearchOpen} />}
      {isUsageLimitModalOpen && organizationId && (
        <UsageLimitModal
          organizationId={organizationId}
          freeTrialLimit={freeTrialLimit}
          isLimitReached={isLimitReached}
          onClose={() => setIsUsageLimitModalOpen(false)}
          isHardMode={isPaymentRequired}
        />
      )}
    </div>
  );
};
